import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ConflictCheckDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showQuickSearch, setShowQuickSearch] = useState(false);
    const [searchTerms, setSearchTerms] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/conflicts/dashboard/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            console.error('Error fetching dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSearch = async (e) => {
        e.preventDefault();
        if (!searchTerms.trim()) return;

        setSearching(true);
        try {
            const token = localStorage.getItem('access_token');
            const terms = searchTerms.split(',').map(t => t.trim()).filter(t => t);

            const response = await fetch('/api/conflicts/quick-search/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ search_terms: terms })
            });

            const data = await response.json();
            setSearchResults(data);
        } catch (err) {
            console.error('Error searching:', err);
        } finally {
            setSearching(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'CLEARED': return 'bg-green-100 text-green-800';
            case 'POTENTIAL': return 'bg-yellow-100 text-yellow-800';
            case 'CONFLICT': return 'bg-red-100 text-red-800';
            case 'WAIVED': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Conflict Check</h1>
                <p className="text-gray-600 mt-1">Check for conflicts of interest before taking new matters</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Link
                    to="/conflicts/new"
                    className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    New Conflict Check
                </Link>
                <button
                    onClick={() => setShowQuickSearch(!showQuickSearch)}
                    className="flex items-center justify-center p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Quick Search
                </button>
                <Link
                    to="/conflicts/history"
                    className="flex items-center justify-center p-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    View History
                </Link>
            </div>

            {/* Quick Search Panel */}
            {showQuickSearch && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Conflict Search</h3>
                    <form onSubmit={handleQuickSearch} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Search Terms (comma-separated)
                            </label>
                            <input
                                type="text"
                                value={searchTerms}
                                onChange={(e) => setSearchTerms(e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                placeholder="e.g., John Smith, ABC Company, Jane Doe"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={searching}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {searching ? 'Searching...' : 'Search'}
                        </button>
                    </form>

                    {searchResults && (
                        <div className="mt-6">
                            <div className={`p-4 rounded-lg ${searchResults.status === 'CLEARED' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                                }`}>
                                <div className="flex items-center mb-2">
                                    {searchResults.status === 'CLEARED' ? (
                                        <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    )}
                                    <span className="font-medium">
                                        {searchResults.status === 'CLEARED'
                                            ? 'No Conflicts Found'
                                            : `${searchResults.total_matches} Potential Match(es) Found`}
                                    </span>
                                </div>

                                {searchResults.potential_conflicts?.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        {searchResults.potential_conflicts.slice(0, 5).map((conflict, idx) => (
                                            <div key={idx} className="bg-white p-3 rounded border border-yellow-200">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="font-medium">{conflict.matched_name}</p>
                                                        <p className="text-sm text-gray-600">
                                                            {conflict.party_type} • Match: {conflict.match_type}
                                                        </p>
                                                    </div>
                                                    <span className="text-sm font-medium text-yellow-700">
                                                        {Math.round(conflict.confidence * 100)}% match
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        {searchResults.total_matches > 5 && (
                                            <p className="text-sm text-gray-500 text-center">
                                                ... and {searchResults.total_matches - 5} more matches
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-yellow-100 rounded-lg">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Pending Review</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.pending_reviews || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Cleared</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.status_summary?.CLEARED || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-red-100 rounded-lg">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Conflicts</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {(dashboardData?.status_summary?.CONFLICT || 0) + (dashboardData?.status_summary?.POTENTIAL || 0)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center">
                        <div className="p-3 bg-purple-100 rounded-lg">
                            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Indexed Parties</p>
                            <p className="text-2xl font-semibold text-gray-900">{dashboardData?.indexed_parties || 0}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Checks */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">Recent Conflict Checks</h2>
                        <Link to="/conflicts/history" className="text-sm text-blue-600 hover:text-blue-700">
                            View All →
                        </Link>
                    </div>
                </div>
                <div className="divide-y divide-gray-100">
                    {dashboardData?.recent_checks?.length > 0 ? (
                        dashboardData.recent_checks.map((check) => (
                            <Link
                                key={check.id}
                                to={`/conflicts/${check.id}`}
                                className="block p-4 hover:bg-gray-50"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-900">{check.prospective_client_name}</p>
                                        <p className="text-sm text-gray-500">
                                            {check.prospective_matter_type || 'General'} • By {check.checked_by_name}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-gray-500">
                                            {check.match_count} match{check.match_count !== 1 ? 'es' : ''}
                                        </span>
                                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(check.status)}`}>
                                            {check.status_display}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <p>No conflict checks yet</p>
                            <Link to="/conflicts/new" className="text-blue-600 hover:underline mt-2 inline-block">
                                Run your first conflict check
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConflictCheckDashboard;
